import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, Directive, DOCUMENT, ElementRef, Inject, Input, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { NgControl, NgForm, NgModel } from '@angular/forms';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Subject } from 'rxjs/internal/Subject';
import { merge } from 'rxjs/internal/observable/merge';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

@Directive({
    selector: 'input[muiInput], textarea[muiInput], select[muiInput]',
    host: {
        '(blur)': 'this.toggleClass($event)',
        '(focus)': 'this.toggleClass($event)',
        '(input)': 'this.toggleClass($event)'
    },
    standalone: true
})
export class MuiInputDirective implements AfterViewInit, OnDestroy {
    @Input()
    muiInputAlwaysFocusedState = false;

    @Input()
    muiInputValidateOnSubmit = true;

    protected labelElement?: HTMLElement;
    protected ngUnsubscribe$: Subject<void> = new Subject<void>();
    private _inputObserver;
    private _listenerInput?: () => void;

    protected control?: NgControl;
    protected form?: NgForm;

    private setClassTimeout?: number;

    protected timeoutService = injectTimeoutService();

    constructor(
        protected elementRef: ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        protected renderer: Renderer2,
        @Inject(DOCUMENT) protected document: Document,
        @Optional() ngForm: NgForm,
        @Optional() ngModel: NgModel,
        @Optional() ngControl: NgControl
    ) {
        if (ngControl) {
            this.control = ngControl;
        }

        if (ngModel) {
            this.control = ngModel;
        }

        if (ngForm) {
            this.form = ngForm;
        }
    }

    @Input()
    set muiInput(label: string) {
        if (label) {
            if (this.labelElement) {
                this.renderer.removeChild(this.labelElement.parentElement, this.labelElement);
                this.labelElement = undefined;
            }

            const labelElement = this.renderer.createElement('label');
            this.renderer.setProperty(labelElement, 'innerText', label);
            this.wrapper.insertBefore(labelElement, this.element.nextSibling);
            this.renderer.addClass(this.wrapper, 'has-label');
            this.labelElement = labelElement;
        }

        if (this.element && this.element.tagName === 'SELECT') {
            this.renderer.addClass(this.wrapper, 'is-select');
        }
    }

    ngAfterViewInit() {
        this.renderer.addClass(this.wrapper, 'mui-input');
        this.setClassTimeout = this.timeoutService.setTimeout(() => this.toggleClass(), 200);

        if (this.control) {
            merge(this.control.valueChanges, this.control.statusChanges)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.toggleClass());
        }

        this._listenerInput = this.renderer.listen(this.element, 'change', () => {
            this.toggleClass();
        });

        if (typeof MutationObserver !== 'undefined') {
            this._inputObserver = new MutationObserver(() => this.toggleClass()).observe(this.element, {
                childList: true,
                attributes: true,
                subtree: true
            });
        }

        if (this.form) {
            this.form.ngSubmit.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.toggleClass());
        }
    }

    ngOnDestroy() {
        if (this.setClassTimeout) {
            clearTimeout(this.setClassTimeout);
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        if (this._inputObserver) {
            this._inputObserver.disconnect();
        }

        if (this._listenerInput) {
            this._listenerInput();
        }
    }

    toggleClass(event?: Event) {
        /** is not empty */
        const isNotEmpty: boolean = coerceBooleanProperty(!!(this.element.value && this.element.value.length));

        /** is required */
        const isRequired: boolean = coerceBooleanProperty(this.element.required);

        /** is form submitted */
        const isSubmitted: boolean | undefined = this.form ? coerceBooleanProperty(this.form.submitAttempt) : undefined;

        /** element is invalid */
        const isInvalid: boolean = this.element.classList.contains('ng-invalid');

        /** element is valid */
        const isValid: boolean = this.element.classList.contains('ng-valid');

        /** if input is disabled */
        const isDisabled = this.element.disabled;

        if (isNotEmpty) {
            this.renderer.addClass(this.wrapper, 'is-not-empty');
        } else {
            this.renderer.removeClass(this.wrapper, 'is-not-empty');
        }

        if (!this.control) {
            const invalid =
                (this.form && this.muiInputValidateOnSubmit ? isSubmitted : isInvalid && isRequired) && isNotEmpty && !isDisabled;

            if (invalid) {
                this.renderer.addClass(this.wrapper, 'is-invalid');
            } else {
                this.renderer.removeClass(this.wrapper, 'is-invalid');
            }

            if (isValid && isRequired && isNotEmpty) {
                this.renderer.addClass(this.wrapper, 'is-valid');
            } else {
                this.renderer.removeClass(this.wrapper, 'is-valid');
            }
        } else {
            const invalid = this.control.invalid && (this.form && this.muiInputValidateOnSubmit ? isSubmitted : isNotEmpty) && !isDisabled;

            if (invalid) {
                this.renderer.addClass(this.wrapper, 'is-invalid');
            } else {
                this.renderer.removeClass(this.wrapper, 'is-invalid');
            }

            if (this.control.valid && isRequired) {
                this.renderer.addClass(this.wrapper, 'is-valid');
            } else {
                this.renderer.removeClass(this.wrapper, 'is-valid');
            }

            if (this.control.pending) {
                this.renderer.addClass(this.wrapper, 'is-pending');
            } else {
                this.renderer.removeClass(this.wrapper, 'is-pending');
            }
        }

        if (this.muiInputAlwaysFocusedState) {
            this.renderer.addClass(this.wrapper, 'is-focused');
        } else {
            if (event && event.type) {
                if (event.type === 'focus') {
                    this.renderer.addClass(this.wrapper, 'is-focused');
                } else if (event.type === 'blur') {
                    this.renderer.removeClass(this.wrapper, 'is-focused');
                }
            }
        }
    }

    get element(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
        return this.elementRef.nativeElement;
    }

    get wrapper(): HTMLElement {
        let element = this.element as HTMLElement;
        do {
            element = element.parentElement as HTMLElement;
        } while (element?.hasAttribute('noMuiWrapper'));
        return element;
    }
}
